import { Card, CardHeader, CardContent, Box, Typography, Chip } from '@mui/material'
import React from 'react'
import { blueSky, spacingItem } from '../../theme'
import { CustomerDto } from '../../../interactors/gen/backendClient'
import { getBestCustomerTypeLabel, getReadableCustomerType } from '../../../domain/Customer/CustomerDisplay'
import { useAccountsStore } from '../../../store/AccountsStore'
import { EditTheAssociationPrompt } from './EditTheAssociationPrompt'
import { getReadableSpecies } from '../../../domain/Animal/AnimalDisplay'

interface Props {
  association: CustomerDto
  onSubmit: (data: Partial<CustomerDto>) => Promise<void>
}

export const AssociationTheAssociationCard: React.FC<Props> = ({ association, onSubmit }) => {
  const accountsStore = useAccountsStore()

  const labelSx = { fontSize: 12, fontWeight: 800, color: '#7A8197', lineHeight: 1.2 }
  const valueSx = { fontSize: 13.5, fontWeight: 700, color: '#1F3764', lineHeight: 1.25 }
  const rowSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '240px 1fr' },
    columnGap: 2,
    rowGap: 0.5,
    alignItems: 'baseline',
    py: 1,
    borderBottom: '1px solid rgba(122, 129, 151, 0.14)',
    '&:last-of-type': { borderBottom: 'none' },
  }

  return (
    <Card sx={{ ...spacingItem, height: 'calc(100% - 16px)', boxSizing: 'border-box' }}>
      <CardHeader
        title={`${getBestCustomerTypeLabel(association?.types, { the: true })}`}
        action={
          accountsStore.canEdit() ? (
            <EditTheAssociationPrompt association={association} onSubmit={onSubmit} />
          ) : undefined
        }
      />
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box sx={rowSx}>
              <Typography sx={labelSx}>Type d&#39;organisation</Typography>
              <Typography sx={valueSx}>
                {association?.types
                  ? association.types.map((type) => getReadableCustomerType(type)).join(', ')
                  : 'Non renseigné'}
              </Typography>
            </Box>

            <Box sx={rowSx}>
              <Typography sx={labelSx}>Nom de l&#39;association</Typography>
              <Typography sx={valueSx}>{association?.name ? association.name : 'Non renseigné'}</Typography>
            </Box>

            <Box sx={rowSx}>
              <Typography sx={labelSx}>Numéro de SIRET</Typography>
              <Typography sx={valueSx}>
                {association?.SIRETNumber ? association.SIRETNumber : 'Non renseigné'}
              </Typography>
            </Box>

            <Box sx={rowSx}>
              <Typography sx={labelSx}>Numéro de RNA</Typography>
              <Typography sx={valueSx}>{association?.RNANumber ? association.RNANumber : 'Non renseigné'}</Typography>
            </Box>

            <Box sx={rowSx}>
              <Typography sx={labelSx}>Adresse de domiciliation</Typography>
              <Typography sx={valueSx}>
                {association?.address ? association.address : 'Non renseigné'}
                <br />
                {association?.postalCode ? association.postalCode : 'Non renseigné'}{' '}
                {association?.city ? association.city : 'Non renseigné'}
              </Typography>
            </Box>

            <Box sx={rowSx}>
              <Typography sx={labelSx}>Email de contact</Typography>
              <Typography sx={valueSx}>{association?.email ? association.email : 'Non renseigné'}</Typography>
            </Box>

            <Box sx={rowSx}>
              <Typography sx={labelSx}>Téléphone de contact</Typography>
              <Typography sx={valueSx}>
                {association?.phoneNumber ? association.phoneNumber : 'Non renseigné'}
              </Typography>
            </Box>

            <Box sx={rowSx}>
              <Typography sx={labelSx}>Animaux pris en charge / an</Typography>
              <Typography sx={valueSx}>
                {association?.howManyAnimalsPerYear ? association.howManyAnimalsPerYear : 'Non renseigné'}
              </Typography>
            </Box>

            <Box sx={rowSx}>
              <Typography sx={labelSx}>Animaux adoptés / an</Typography>
              <Typography sx={valueSx}>
                {association?.howManyAdoptionsPerYear ? association.howManyAdoptionsPerYear : 'Non renseigné'}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'left',
                width: '100%',
                gap: 1,
              }}
            >
              <Typography sx={{ ...labelSx, fontSize: 12 }}>
                {`${getBestCustomerTypeLabel(association?.types, { the: true })} prend en charge`}:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {association?.preferences?.species.map((species) => (
                  <Chip
                    key={species}
                    sx={{ backgroundColor: blueSky, color: 'white', fontWeight: 500 }}
                    label={getReadableSpecies(species, { withEmoji: false })}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
